import { useState, useEffect, useMemo } from 'react';
import { Save, Plus, Trash2, ChevronDown, ChevronRight, Info, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';

// ─── Leave type defaults ───────────────────────────────────────────────────────

const DEFAULT_LEAVE_TYPES = [
  { code: 'EL',   name: 'Earned Leave',    color: '#22c55e', annual_quota: 15, accrual_method: 'MONTHLY', carry_forward: true,  carry_forward_max: 30, encashable: true,  max_encashable: 30, half_day: true,  gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME'], probation: false, notice: false, statutory_basis: 'Factories Act 1948 / Shops & Estab. Acts', is_active: true },
  { code: 'CL',   name: 'Casual Leave',    color: '#3b82f6', annual_quota: 12, accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0,  encashable: false, max_encashable: 0,  half_day: true,  gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME'], probation: true,  notice: false, statutory_basis: 'Shops & Establishments Acts', is_active: true },
  { code: 'SL',   name: 'Sick Leave',      color: '#f59e0b', annual_quota: 12, accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 5,  encashable: false, max_encashable: 0,  half_day: false, gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME'], probation: true,  notice: true,  statutory_basis: 'Shops & Establishments Acts', is_active: true },
  { code: 'ML',   name: 'Maternity Leave', color: '#ec4899', annual_quota: 182, accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0, encashable: false, max_encashable: 0,  half_day: false, gender: 'FEMALE', emp_types: ['FULL_TIME'], probation: true, notice: true, statutory_basis: 'Maternity Benefit Act 1961 (amended 2017) — 182d/84d rule applies', is_active: true },
  { code: 'PTL',  name: 'Paternity Leave', color: '#8b5cf6', annual_quota: 5,  accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0,  encashable: false, max_encashable: 0,  half_day: false, gender: 'MALE',   emp_types: ['FULL_TIME'], probation: true, notice: true, statutory_basis: 'Company policy (no central statute)', is_active: true },
  { code: 'AL',   name: 'Adoption Leave',  color: '#14b8a6', annual_quota: 84, accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0,  encashable: false, max_encashable: 0,  half_day: false, gender: 'FEMALE', emp_types: ['FULL_TIME'], probation: true, notice: true, statutory_basis: 'Maternity Benefit Act 1961 (Sec 5A) — 84 days', is_active: true },
  { code: 'BL',   name: 'Bereavement Leave', color: '#6b7280', annual_quota: 3, accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0, encashable: false, max_encashable: 0,  half_day: false, gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME'], probation: true, notice: true, statutory_basis: 'Company policy', is_active: true },
  { code: 'MAL',  name: 'Marriage Leave',  color: '#f97316', annual_quota: 3,  accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0,  encashable: false, max_encashable: 0,  half_day: false, gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME'], probation: false, notice: false, statutory_basis: 'Company policy', is_active: true },
  { code: 'CO',   name: 'Comp-Off',        color: '#0ea5e9', annual_quota: 0,  accrual_method: 'MANUAL',  carry_forward: true,  carry_forward_max: 60, encashable: false, max_encashable: 0,  half_day: true,  gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME'], probation: false, notice: false, statutory_basis: 'Company policy — CF expires in 60 days', is_active: true },
  { code: 'OH',   name: 'Optional Holiday', color: '#84cc16', annual_quota: 2, accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0,  encashable: false, max_encashable: 0,  half_day: false, gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME'], probation: true, notice: false, statutory_basis: 'Government restricted holiday list', is_active: true },
  { code: 'SPL',  name: 'Special Leave',   color: '#a855f7', annual_quota: 0,  accrual_method: 'MANUAL',  carry_forward: false, carry_forward_max: 0,  encashable: false, max_encashable: 0,  half_day: true,  gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME','CONTRACT'], probation: true, notice: true, statutory_basis: 'Company discretion — case-by-case', is_active: true },
  { code: 'LWP',  name: 'Loss of Pay',     color: '#ef4444', annual_quota: 0,  accrual_method: 'AUTO',    carry_forward: false, carry_forward_max: 0,  encashable: false, max_encashable: 0,  half_day: true,  gender: 'ALL',    emp_types: ['FULL_TIME','PART_TIME','CONTRACT','DAILY_WAGE'], probation: true, notice: true, statutory_basis: 'Auto-triggered — no balance remaining', is_active: true },
];

const STATE_PRESETS = {
  Maharashtra: { EL: 21, CL: 8, SL: 14, basis: 'Maharashtra Shops & Establishments Act 1948' },
  Karnataka:   { EL: 15, CL: 12, SL: 12, basis: 'Karnataka Shops & Commercial Establishments Act 1961' },
  Delhi:       { EL: 15, CL: 14, SL: 12, basis: 'Delhi Shops & Establishments Act 1954' },
  'Tamil Nadu':{ EL: 12, CL: 12, SL: 6,  basis: 'Tamil Nadu Shops & Establishments Act 1947' },
  Gujarat:     { EL: 15, CL: 12, SL: 12, basis: 'Gujarat Shops & Establishments Act 1948' },
  Telangana:   { EL: 15, CL: 12, SL: 12, basis: 'Telangana Shops & Establishments Act 1988' },
};

const ACCRUAL_OPTIONS  = ['UPFRONT', 'MONTHLY', 'QUARTERLY', 'MANUAL', 'AUTO'];
const GENDER_OPTIONS   = ['ALL', 'MALE', 'FEMALE'];
const EMP_TYPE_OPTIONS = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'DAILY_WAGE'];

// ─── Sub-components ───────────────────────────────────────────────────────────

function Toggle({ on, onChange }) {
  return (
    <button onClick={() => onChange(!on)}
      className={cn('w-9 h-5 rounded-full transition-colors relative shrink-0', on ? 'bg-[var(--accent)]' : 'bg-[var(--border)]')}>
      <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all', on ? 'left-4' : 'left-0.5')} />
    </button>
  );
}

function NumInput({ value, onChange, min = 0, width = 'w-16' }) {
  return (
    <input type="number" min={min} value={value}
      onChange={e => onChange(Number(e.target.value))}
      className={cn('h-8 px-2 text-center text-[12px] border border-[var(--border)] rounded-[7px] bg-[var(--bg-app)] focus:outline-none focus:border-[var(--accent)] text-[var(--text-primary)]', width)} />
  );
}

// ─── Tab 1 — Leave Types ──────────────────────────────────────────────────────

function LeaveTypesTab({ types, onUpdate }) {
  const [expanded, setExpanded] = useState(null);
  const [statePreset, setStatePreset] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const applyStatePreset = (state) => {
    const preset = STATE_PRESETS[state];
    if (!preset) return;
    onUpdate(types.map(t => {
      if (preset[t.code] !== undefined) return { ...t, annual_quota: preset[t.code] };
      return t;
    }));
    toast.success(`Applied ${state} defaults (EL ${preset.EL}d, CL ${preset.CL}d, SL ${preset.SL}d)`);
    setStatePreset('');
  };

  const update = (code, key, val) =>
    onUpdate(types.map(t => t.code === code ? { ...t, [key]: val } : t));

  const remove = (code) => {
    if (['EL','CL','SL','ML','LWP'].includes(code)) { toast.error('Statutory leave types cannot be deleted'); return; }
    onUpdate(types.filter(t => t.code !== code));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <select value={statePreset} onChange={e => applyStatePreset(e.target.value)}
            className="h-8 px-3 text-[12px] border border-[var(--border)] rounded-[8px] bg-[var(--bg-card)] text-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent)]">
            <option value="">Load State Defaults…</option>
            {Object.keys(STATE_PRESETS).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-1 text-[11px] text-[var(--text-tertiary)]">
            <Info size={11} /> Applies EL/CL/SL quotas only
          </div>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
          <Plus size={13} /> Add Custom Type
        </button>
      </div>

      <div className="card p-0 overflow-hidden">
        {types.map((t, idx) => (
          <div key={t.code} className="border-b border-[var(--border)] last:border-0">
            {/* Row header */}
            <div className={cn('flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[var(--bg-hover)] transition-colors', !t.is_active && 'opacity-50')}>
              <div className="w-3 h-3 rounded-full shrink-0" style={{ background: t.color }} />
              <button onClick={() => setExpanded(expanded === t.code ? null : t.code)} className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[13px] font-semibold text-[var(--text-primary)]">{t.name}</span>
                <span className="text-[10px] font-mono bg-[var(--bg-hover)] px-1.5 py-0.5 rounded text-[var(--text-tertiary)]">{t.code}</span>
                <span className="text-[11px] text-[var(--text-tertiary)] truncate hidden sm:block">— {t.statutory_basis}</span>
                {expanded === t.code ? <ChevronDown size={13} className="text-[var(--text-tertiary)] ml-auto" /> : <ChevronRight size={13} className="text-[var(--text-tertiary)] ml-auto" />}
              </button>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <NumInput value={t.annual_quota} onChange={v => update(t.code, 'annual_quota', v)} />
                  <p className="text-[9px] text-[var(--text-tertiary)] text-center mt-0.5">days/yr</p>
                </div>
                <Toggle on={t.is_active} onChange={v => update(t.code, 'is_active', v)} />
                {!['EL','CL','SL','ML','LWP'].includes(t.code) && (
                  <button onClick={() => remove(t.code)} className="text-[var(--text-tertiary)] hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>

            {/* Expanded config */}
            {expanded === t.code && (
              <div className="px-5 pb-4 pt-1 bg-[var(--bg-app)] border-t border-[var(--border)]">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-[12px]">
                  <div>
                    <label className="form-label">Accrual Method</label>
                    <select value={t.accrual_method} onChange={e => update(t.code, 'accrual_method', e.target.value)} className="input-field text-[12px]">
                      {ACCRUAL_OPTIONS.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Gender Restriction</label>
                    <select value={t.gender} onChange={e => update(t.code, 'gender', e.target.value)} className="input-field text-[12px]">
                      {GENDER_OPTIONS.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Max Carry Forward (days)</label>
                    <NumInput value={t.carry_forward_max} onChange={v => update(t.code, 'carry_forward_max', v)} width="w-20" />
                  </div>
                  <div>
                    <label className="form-label">Max Encashable (days)</label>
                    <NumInput value={t.max_encashable} onChange={v => update(t.code, 'max_encashable', v)} width="w-20" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="form-label">Options</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Toggle on={t.carry_forward} onChange={v => update(t.code, 'carry_forward', v)} />
                      <span>Carry Forward</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Toggle on={t.encashable} onChange={v => update(t.code, 'encashable', v)} />
                      <span>Encashable</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Toggle on={t.half_day} onChange={v => update(t.code, 'half_day', v)} />
                      <span>Half Day</span>
                    </label>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="form-label">During Probation/Notice</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Toggle on={t.probation} onChange={v => update(t.code, 'probation', v)} />
                      <span>Allowed in Probation</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Toggle on={t.notice} onChange={v => update(t.code, 'notice', v)} />
                      <span>Allowed in Notice Period</span>
                    </label>
                  </div>
                  <div className="col-span-2">
                    <label className="form-label">Applicable Employment Types</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {EMP_TYPE_OPTIONS.map(et => (
                        <label key={et} className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={t.emp_types?.includes(et)}
                            onChange={e => {
                              const cur = t.emp_types || [];
                              update(t.code, 'emp_types', e.target.checked ? [...cur, et] : cur.filter(x => x !== et));
                            }}
                            className="h-3.5 w-3.5 rounded accent-[var(--accent)]" />
                          <span className="text-[12px] text-[var(--text-secondary)]">{et.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                {t.code === 'ML' && (
                  <div className="mt-3 p-2.5 rounded-[8px] bg-amber-50 border border-amber-200 text-[11px] text-amber-700 flex items-start gap-2">
                    <AlertCircle size={13} className="shrink-0 mt-0.5" />
                    <span>Maternity Benefit Act 1961 (amended 2017): 182 days (26 weeks) for 1st & 2nd child; 84 days (12 weeks) for 3rd child onwards. Adoption leave is 84 days. HR must select applicable rule per employee when approving ML request.</span>
                  </div>
                )}
                {t.code === 'EL' && (
                  <div className="mt-3 p-2.5 rounded-[8px] bg-blue-50 border border-blue-200 text-[11px] text-blue-700 flex items-start gap-2">
                    <AlertCircle size={13} className="shrink-0 mt-0.5" />
                    <span>Factories Act 1948 (Sec 79): 1 day EL per 20 days worked. Shops Acts vary by state. See state defaults above. Minimum 15 days/yr for most states; 21 days for Maharashtra. EL encashment basis is Basic salary (÷26 per day).</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && <AddCustomTypeModal onSave={newType => { onUpdate([...types, newType]); setShowAdd(false); }} onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function AddCustomTypeModal({ onSave, onClose }) {
  const [form, setForm] = useState({ code: '', name: '', color: '#6b7280', annual_quota: 0, accrual_method: 'UPFRONT', carry_forward: false, carry_forward_max: 0, encashable: false, max_encashable: 0, half_day: true, gender: 'ALL', emp_types: ['FULL_TIME', 'PART_TIME'], probation: true, notice: true, statutory_basis: 'Company policy', is_active: true });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error('Code and name are required'); return; }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl w-full max-w-md border border-[var(--border)] shadow-[var(--shadow-lg)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Add Custom Leave Type</h3>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Code *</label>
              <input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="e.g. FL" maxLength={5} className="input-field" />
            </div>
            <div>
              <label className="form-label">Name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Festival Leave" className="input-field" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Days/Year</label>
              <NumInput value={form.annual_quota} onChange={v => set('annual_quota', v)} width="w-full" />
            </div>
            <div>
              <label className="form-label">Color</label>
              <input type="color" value={form.color} onChange={e => set('color', e.target.value)} className="h-9 w-full cursor-pointer rounded-[8px] border border-[var(--border)]" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[var(--border)]">
          <button onClick={onClose} className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">Cancel</button>
          <button onClick={handleSave} className="h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors">Add Type</button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 2 — Policy Assignment ────────────────────────────────────────────────

const DEFAULT_ASSIGNMENTS = [
  { id: 'scope-default', scope_type: 'DEFAULT', scope_label: 'All Employees', quotas: { EL: 15, CL: 12, SL: 12 } },
];

function PolicyAssignmentTab({ assignments, leaveTypes, onUpdate }) {
  const [showAdd, setShowAdd] = useState(false);

  const update = (id, code, val) =>
    onUpdate(assignments.map(a => a.id === id ? { ...a, quotas: { ...a.quotas, [code]: Number(val) } } : a));

  const remove = (id) => {
    if (id === 'scope-default') { toast.error('Default scope cannot be removed'); return; }
    onUpdate(assignments.filter(a => a.id !== id));
  };

  const encashableTypes = leaveTypes.filter(t => ['EL', 'CL', 'SL'].includes(t.code) && t.is_active);

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)] text-[12px] text-[var(--text-secondary)]">
        <strong>Precedence:</strong> Designation override → Department override → Employment Type override → Default. More specific rules always win.
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">Scope Overrides</h3>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
          <Plus size={13} /> Add Override
        </button>
      </div>

      <div className="card p-0 overflow-hidden overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">Scope</th>
              {encashableTypes.map(t => (
                <th key={t.code} className="px-4 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap" style={{ color: t.color }}>
                  {t.code}
                </th>
              ))}
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {assignments.map(a => (
              <tr key={a.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors">
                <td className="px-4 py-2.5">
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">{a.scope_label}</p>
                    <p className="text-[10px] text-[var(--text-tertiary)]">{a.scope_type}{a.scope_type === 'DEFAULT' ? ' (fallback)' : ''}</p>
                  </div>
                </td>
                {encashableTypes.map(t => (
                  <td key={t.code} className="px-4 py-2.5 text-center">
                    <input type="number" min={0} value={a.quotas?.[t.code] ?? '—'}
                      onChange={e => update(a.id, t.code, e.target.value)}
                      className="h-8 w-14 text-center text-[12px] border border-[var(--border)] rounded-[6px] bg-[var(--bg-app)] focus:outline-none focus:border-[var(--accent)]" />
                  </td>
                ))}
                <td className="px-4 py-2.5">
                  {a.id !== 'scope-default' && (
                    <button onClick={() => remove(a.id)} className="text-[var(--text-tertiary)] hover:text-red-500 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddScopeModal leaveTypes={encashableTypes} onSave={sc => { onUpdate([...assignments, { ...sc, id: `scope-${Date.now()}` }]); setShowAdd(false); }} onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function AddScopeModal({ leaveTypes, onSave, onClose }) {
  const [form, setForm] = useState({ scope_type: 'DEPARTMENT', scope_label: '', quotas: {} });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl w-full max-w-lg border border-[var(--border)] shadow-[var(--shadow-lg)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Add Scope Override</h3>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Scope Type</label>
              <select value={form.scope_type} onChange={e => set('scope_type', e.target.value)} className="input-field">
                <option value="DEPARTMENT">Department</option>
                <option value="DESIGNATION">Designation</option>
                <option value="EMPLOYMENT_TYPE">Employment Type</option>
              </select>
            </div>
            <div>
              <label className="form-label">Name / Label</label>
              <input value={form.scope_label} onChange={e => set('scope_label', e.target.value)} placeholder="e.g. Factory Workers" className="input-field" />
            </div>
          </div>
          <div>
            <label className="form-label">Quota Overrides (days/year)</label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {leaveTypes.map(t => (
                <div key={t.code}>
                  <label className="text-[11px] font-medium" style={{ color: t.color }}>{t.name}</label>
                  <input type="number" min={0} value={form.quotas[t.code] || ''}
                    onChange={e => setForm(f => ({ ...f, quotas: { ...f.quotas, [t.code]: Number(e.target.value) } }))}
                    placeholder="inherit"
                    className="mt-1 h-8 w-full px-2 text-[12px] border border-[var(--border)] rounded-[7px] bg-[var(--bg-app)] focus:outline-none focus:border-[var(--accent)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[var(--border)]">
          <button onClick={onClose} className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">Cancel</button>
          <button onClick={() => { if (!form.scope_label) { toast.error('Enter a label'); return; } onSave(form); }}
            className="h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors">
            Add Override
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 3 — Holiday Calendar ─────────────────────────────────────────────────

const NATIONAL_HOLIDAYS_2026 = [
  { date: '2026-01-01', name: "New Year's Day", type: 'NATIONAL' },
  { date: '2026-01-26', name: 'Republic Day', type: 'NATIONAL' },
  { date: '2026-03-25', name: 'Holi', type: 'NATIONAL' },
  { date: '2026-04-14', name: 'Dr. Ambedkar Jayanti', type: 'NATIONAL' },
  { date: '2026-05-01', name: 'Labour Day / Maharashtra Day', type: 'NATIONAL' },
  { date: '2026-08-15', name: 'Independence Day', type: 'NATIONAL' },
  { date: '2026-10-02', name: 'Gandhi Jayanti', type: 'NATIONAL' },
  { date: '2026-10-20', name: 'Diwali (Lakshmi Puja)', type: 'NATIONAL' },
  { date: '2026-11-05', name: 'Guru Nanak Jayanti', type: 'NATIONAL' },
  { date: '2026-12-25', name: 'Christmas Day', type: 'NATIONAL' },
];

const HOLIDAY_COLORS = {
  NATIONAL: 'bg-blue-100 text-blue-700',
  STATE:    'bg-purple-100 text-purple-700',
  OPTIONAL: 'bg-amber-100 text-amber-700',
  COMPANY:  'bg-green-100 text-green-700',
};

function HolidayCalendarTab({ holidays, onUpdate }) {
  const [form, setForm] = useState({ date: '', name: '', type: 'NATIONAL' });
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());

  const visible = holidays.filter(h => h.date?.startsWith(yearFilter));
  const sorted  = [...visible].sort((a, b) => a.date.localeCompare(b.date));

  const addHoliday = () => {
    if (!form.date || !form.name.trim()) { toast.error('Date and name are required'); return; }
    if (holidays.some(h => h.date === form.date)) { toast.error('A holiday already exists on this date'); return; }
    onUpdate([...holidays, { ...form, id: `h-${Date.now()}` }]);
    setForm({ date: '', name: '', type: 'NATIONAL' });
  };

  const remove = (id) => onUpdate(holidays.filter(h => h.id !== id && h.date !== id));

  const loadNational = () => {
    const existing = new Set(holidays.map(h => h.date));
    const toAdd    = NATIONAL_HOLIDAYS_2026.filter(h => !existing.has(h.date)).map((h, i) => ({ ...h, id: `nat-${i}` }));
    onUpdate([...holidays, ...toAdd]);
    toast.success(`Added ${toAdd.length} national holidays for 2026`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
            className="h-8 px-3 text-[12px] border border-[var(--border)] rounded-[8px] bg-[var(--bg-card)] text-[var(--text-secondary)] focus:outline-none">
            {['2025','2026','2027'].map(y => <option key={y}>{y}</option>)}
          </select>
          <span className="text-[12px] text-[var(--text-tertiary)]">{sorted.length} holidays</span>
        </div>
        <button onClick={loadNational}
          className="h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
          Load National Holidays 2026
        </button>
      </div>

      {/* Add form */}
      <div className="flex items-end gap-2 flex-wrap p-3 rounded-[10px] bg-[var(--bg-app)] border border-[var(--border)]">
        <div className="flex-1 min-w-[130px]">
          <label className="form-label">Date</label>
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="input-field" />
        </div>
        <div className="flex-[2] min-w-[180px]">
          <label className="form-label">Holiday Name</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Diwali" className="input-field" />
        </div>
        <div>
          <label className="form-label">Type</label>
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="input-field">
            <option value="NATIONAL">National</option>
            <option value="STATE">State</option>
            <option value="COMPANY">Company</option>
            <option value="OPTIONAL">Optional</option>
          </select>
        </div>
        <button onClick={addHoliday}
          className="h-9 px-4 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors self-end">
          Add
        </button>
      </div>

      {/* Holiday list */}
      {sorted.length === 0 ? (
        <div className="text-center py-8 text-[var(--text-tertiary)] text-[13px]">No holidays added for {yearFilter}</div>
      ) : (
        <div className="card p-0 overflow-hidden">
          {sorted.map(h => (
            <div key={h.id || h.date} className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors">
              <div className="w-20 shrink-0">
                <p className="text-[12px] font-mono text-[var(--text-secondary)]">
                  {h.date ? new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                </p>
                <p className="text-[10px] text-[var(--text-tertiary)]">
                  {h.date ? new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short' }) : ''}
                </p>
              </div>
              <p className="flex-1 text-[13px] font-medium text-[var(--text-primary)]">{h.name}</p>
              <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold', HOLIDAY_COLORS[h.type] || HOLIDAY_COLORS.COMPANY)}>
                {h.type}
              </span>
              <button onClick={() => remove(h.id || h.date)} className="text-[var(--text-tertiary)] hover:text-red-500 transition-colors">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = ['Leave Types', 'Policy Assignment', 'Holiday Calendar'];

export default function LeavePoliciesSettingsPage() {
  const { data }        = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();
  const [tab, setTab]   = useState(0);

  const [leaveTypes,   setLeaveTypes]   = useState(DEFAULT_LEAVE_TYPES);
  const [assignments,  setAssignments]  = useState(DEFAULT_ASSIGNMENTS);
  const [holidays,     setHolidays]     = useState([]);

  useEffect(() => {
    const s = data?.data?.settings;
    if (!s) return;
    if (s.leave_types?.length)     setLeaveTypes(s.leave_types);
    if (s.policy_assignments?.length) setAssignments(s.policy_assignments);
    if (s.holidays?.length)        setHolidays(s.holidays);
  }, [data]);

  const handleSave = () => {
    mutate({
      settings: {
        leave_types:        leaveTypes,
        policy_assignments: assignments,
        holidays:           holidays,
        // legacy compat
        leave_policies:     leaveTypes.map(t => ({ type: t.name, code: t.code, total: t.annual_quota, carry_forward: t.carry_forward, max_carry: t.carry_forward_max, encashable: t.encashable })),
      },
    }, {
      onSuccess: () => toast.success('Leave policies saved'),
      onError:   () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <PageHeader title="Leave Policies" breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Leave Policies' }]} />
        <button onClick={handleSave} disabled={isPending}
          className="flex items-center gap-2 h-9 px-4 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors">
          <Save size={14} /> {isPending ? 'Saving…' : 'Save All Changes'}
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[var(--border)]">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={cn('px-4 py-2.5 text-[13px] font-medium border-b-2 transition-all -mb-px',
              tab === i ? 'border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]')}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div>
        {tab === 0 && <LeaveTypesTab types={leaveTypes} onUpdate={setLeaveTypes} />}
        {tab === 1 && <PolicyAssignmentTab assignments={assignments} leaveTypes={leaveTypes} onUpdate={setAssignments} />}
        {tab === 2 && <HolidayCalendarTab holidays={holidays} onUpdate={setHolidays} />}
      </div>
    </div>
  );
}
